<script lang="ts">
  import { onMount } from 'svelte';
  import { Hospital, Episodios, Pacientes, type Episodio } from '../lib/api';
  import { mapaNombresPaciente, etiquetaVisible, completarNombresPaciente } from '../lib/nombres';

  let episodios = $state<Episodio[]>([]);
  let nombresPac = $state<Record<string, string>>({});
  let error = $state<string | null>(null);
  let nuevaAlta = $state<any>({
    episodioId: '', pacienteId: '', diagnosticoEgreso: '', condicionEgreso: 'MEJORADO',
    indicaciones: '', recetas: '', cuidadosEnCasa: '', proximaCita: '', firmadaPor: ''
  });
  let ultimaCreada = $state<string | null>(null);

  async function cargar() {
    try {
      episodios = await Episodios.abiertos('HOSPITALIZACION');
      const pacs = await Pacientes.list().catch(() => []);
      nombresPac = await completarNombresPaciente(
        mapaNombresPaciente(pacs),
        episodios.map(e => e.pacienteId),
      );
    } catch (e: any) { error = e?.message; }
  }

  async function firmar(e: SubmitEvent) {
    e.preventDefault();
    try {
      const r = await Hospital.altaFirmar({ ...nuevaAlta });
      ultimaCreada = r?.episodioId ?? nuevaAlta.episodioId;
      nuevaAlta = { episodioId:'', pacienteId:'', diagnosticoEgreso:'', condicionEgreso:'MEJORADO', indicaciones:'', recetas:'', cuidadosEnCasa:'', proximaCita:'', firmadaPor:'' };
      await cargar();
    } catch (e: any) { error = e?.message; }
  }

  function seleccionar(ep: Episodio) {
    nuevaAlta = { ...nuevaAlta, episodioId: ep.id!, pacienteId: ep.pacienteId };
  }

  onMount(() => { void cargar(); });
</script>

<section>
  <header class="view-header">
    <div><h2>Altas hospitalarias</h2><p class="muted">Firma de alta y cierre de episodios de hospitalización</p></div>
    <button onclick={cargar}>Recargar</button>
  </header>
  {#if error}<p class="err">⚠ {error}</p>{/if}

  <div class="panel">
    <h3>Episodios activos de hospitalización</h3>
    <table>
      <thead><tr><th>Episodio</th><th>Paciente</th><th>Inicio</th><th></th></tr></thead>
      <tbody>
        {#each episodios as ep (ep.id)}
          <tr class:sel={ep.id===nuevaAlta.episodioId}>
            <td>{ep.numero ?? '—'}</td>
            <td>{etiquetaVisible(nombresPac, ep.pacienteId)}</td>
            <td>{ep.abiertoEn?.slice(0,16).replace('T',' ') ?? ''}</td>
            <td><button onclick={() => seleccionar(ep)}>Dar de alta</button></td>
          </tr>
        {:else}<tr><td colspan="4" class="empty">Sin pacientes hospitalizados activos.</td></tr>{/each}
      </tbody>
    </table>
  </div>

  {#if nuevaAlta.episodioId}
    <div class="panel">
      <h3>Firma de alta · {etiquetaVisible(nombresPac, nuevaAlta.pacienteId, 'paciente')} · {episodios.find(e => e.id === nuevaAlta.episodioId)?.numero ?? ''}</h3>
      <form onsubmit={firmar}>
        <div class="row">
          <label class="field"><span>Condición</span>
            <select bind:value={nuevaAlta.condicionEgreso}>
              <option>MEJORADO</option><option>IGUAL</option><option>EMPEORADO</option><option>FALLECIDO</option><option>ALTA_VOLUNTARIA</option><option>TRASLADO</option>
            </select>
          </label>
          <label class="field"><span>Firmada por</span><input bind:value={nuevaAlta.firmadaPor} required/></label>
          <label class="field"><span>Próxima cita</span><input type="datetime-local" bind:value={nuevaAlta.proximaCita}/></label>
        </div>
        <label class="field full"><span>Diagnóstico de egreso</span><textarea bind:value={nuevaAlta.diagnosticoEgreso} required rows="2"></textarea></label>
        <label class="field full"><span>Indicaciones</span><textarea bind:value={nuevaAlta.indicaciones} rows="3"></textarea></label>
        <label class="field full"><span>Recetas</span><textarea bind:value={nuevaAlta.recetas} rows="3"></textarea></label>
        <label class="field full"><span>Cuidados en casa</span><textarea bind:value={nuevaAlta.cuidadosEnCasa} rows="2"></textarea></label>
        <button class="primary" type="submit">Firmar alta y cerrar episodio</button>
      </form>
    </div>
  {/if}

  {#if ultimaCreada}
    <p class="ok">✓ Alta firmada. <a href={Hospital.altaImprimibleUrl(ultimaCreada)} target="_blank" rel="noopener">Abrir resumen imprimible</a></p>
  {/if}
</section>

<style>
  .view-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; flex-wrap: wrap; margin-bottom: 1rem; }
  .view-header h2 { margin: 0; font-size: 1.4rem; color: #111827; }
  .muted { margin: 0.2rem 0 0; color: #6b7280; font-size: 0.85rem; }
  button { padding: .5rem .9rem; border: 0; background: linear-gradient(135deg, #15803d, #16a34a); color: #fff; border-radius: 8px; cursor: pointer; font: inherit; font-weight: 600; box-shadow: 0 4px 14px rgba(22,163,74,0.3); transition: transform 0.15s; }
  button:hover { transform: translateY(-1px); }
  h3 { margin: 0 0 .5rem; font-size: 1rem; color: #166534; }
  .panel { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 1.25rem; margin: 0 0 1rem; box-shadow: 0 1px 3px rgb(0 0 0 / 5%); }
  table { width: 100%; border-collapse: collapse; margin-bottom: .25rem; }
  th { text-align: left; font-size: .7rem; text-transform: uppercase; letter-spacing: .03em; color: #6b7280; padding: .55rem .8rem; background: #f0fdf4; }
  td { padding: .55rem .8rem; border-top: 1px solid #f3f4f6; text-align: left; font-size: .9rem; color: #111827; }
  tr.sel { background: #f0fdf4; }
  .row { display: flex; gap: .75rem; flex-wrap: wrap; align-items: flex-end; margin: .6rem 0; }
  .field { display: flex; flex-direction: column; gap: .3rem; }
  .field span { font-size: .72rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: .03em; }
  .field.full { width: 100%; margin: .5rem 0; }
  textarea, input, select { padding: .5rem .65rem; border: 1px solid #e5e7eb; border-radius: 8px; font: inherit; background: #fff; color: #111827; }
  textarea:focus, input:focus, select:focus { outline: none; border-color: #16a34a; box-shadow: 0 0 0 3px rgba(22,163,74,0.12); }
  .field input, .field select, .field textarea { width: 100%; box-sizing: border-box; }
  .primary { padding: .6rem 1.2rem; }
  .err { color: #dc2626; } .ok { color: #166534; font-weight: 600; } .empty { color: #9ca3af; text-align: center; font-style: italic; }
  td button { padding: .3rem .6rem; border-radius: 6px; border: 1px solid #e5e7eb; background: #fff; color: #16a34a; cursor: pointer; font-size: .8rem; box-shadow: none; font-weight: 500; }
  td button:hover { background: #f0fdf4; transform: none; }
  .ok a { color: #15803d; }
</style>
